import {
	ClassNameProps,
	Column,
	Conditional,
	Else,
	If,
	makeClassName,
	makeObjectFromArrayMap,
	Row,
	Rule,
	StyleProps,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import {
	LearningObjective,
	LearningObjectiveCategory,
	LearningObjectiveCategoryNameFragment,
	LearningObjectiveNameFragment,
	MarkValue,
	MarkValueNameFragment,
	ReportCardSectionPolicy,
	Student,
	StudentNameAndImageFragment,
} from "../../../../Utilities";
import {
	LearningObjectiveCategoryFormat,
	LearningObjectiveFormat,
	MarkValueFormat,
	ReportCardSectionPolicyFormat,
	StudentFormat,
} from "../../../Format";
import { StudentIcon } from "../../Student";
import styles from "./styles.module.css";

const markValueMaxColumns = 5;

export type LearningObjectivesTableRowProps<T> = {
	rowVariant: "student" | "learningObjective";
	item: BaseLearningObjectivesTableProps<T>["items"][0];
	markValues: BaseLearningObjectivesTableProps<T>["learningObjectiveCategory"]["markingSystem"]["markValues"];
	isSingleMarkValueColumn: boolean;
	NameCellComponent: (props: { item: BaseLearningObjectivesTableProps<T>["items"][0] }) => JSX.Element | null;
	disabled?: boolean;
};

const defaultDisabledItemIDs = new Set<string>();

export type BaseLearningObjectivesTableProps<T> = {
	slim?: boolean;
	rowVariant: "student" | "learningObjective";
	learningObjectiveCategory: Pick<LearningObjectiveCategory, "id"> &
		LearningObjectiveCategoryNameFragment & {
			markingSystem: { markValues: (Pick<MarkValue, "id"> & MarkValueNameFragment)[] };
		};
	items: (T & { id: string })[];
	disabledItemIDs?: Set<string>;
	RowComponent: (props: LearningObjectivesTableRowProps<T>) => JSX.Element | null;
	NameCellComponent: LearningObjectivesTableRowProps<T>["NameCellComponent"];
	disabled?: boolean;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Base table component for LearningObjectives. Generally, you should use either the Form or Display versions of this
 * component.
 */
export function BaseLearningObjectivesTable<T>({
	slim = false,
	rowVariant,
	learningObjectiveCategory,
	items,
	disabledItemIDs = defaultDisabledItemIDs,
	RowComponent,
	NameCellComponent,
	disabled = false,
	className,
	style,
}: BaseLearningObjectivesTableProps<T>) {
	const markValueIDs = learningObjectiveCategory.markingSystem.markValues.map((markValue) => markValue.id);
	const columns = ["name"];
	let columnWidths: Record<string, string> | undefined;
	const isSingleMarkValueColumn = markValueIDs.length > markValueMaxColumns; // could be updated to be based on screen size
	if (isSingleMarkValueColumn) {
		columns.push("markValue");
		columnWidths = { markValue: "7rem" };
	} else {
		columns.push(...markValueIDs);
		columnWidths = makeObjectFromArrayMap(markValueIDs, (id) => [id, "7rem"]);
	}

	// TODO hex-core-update simplify disabling internal scroll
	return (
		<Table
			key={learningObjectiveCategory.id}
			columns={columns}
			columnWidths={columnWidths}
			className={makeClassName(styles["los-table"], className)}
			style={{ overflow: "visible", ...style }}
		>
			<Table.Header className={styles["los-table__header"]} style={{ overflow: "visible" }}>
				<If condition={!slim && rowVariant === "learningObjective"}>
					<Table.Row className={styles["los-table__header__row"]}>
						<Table.Heading
							column={columns[0]}
							colspan={columns.length}
							noResize
							className={styles["los-table__header__heading"] + " " + styles["los-table__header__category-heading"]}
						>
							{LearningObjectiveCategoryFormat.name(learningObjectiveCategory)}
						</Table.Heading>
					</Table.Row>
				</If>
				<Table.Row className={styles["los-table__header__row"]}>
					<Table.Heading column="name" noResize className={styles["los-table__header__heading"]}>
						<Conditional>
							<If condition={rowVariant === "student"}>
								<span>Student</span>
							</If>
							<If condition={slim}>
								<span>{LearningObjectiveCategoryFormat.name(learningObjectiveCategory)}</span>
							</If>
							<Else>
								<span>Aspect</span>
							</Else>
						</Conditional>
					</Table.Heading>
					<Conditional>
						<If condition={isSingleMarkValueColumn}>
							<Table.Heading
								column="markValue"
								noResize
								className={
									styles["los-table__header__heading"] + " " + styles["los-table__header__learning-objective-heading"]
								}
							>
								Mark
							</Table.Heading>
						</If>
						<Else>
							{learningObjectiveCategory.markingSystem.markValues.map((markValue) => (
								<Table.Heading
									key={markValue.id}
									column={markValue.id}
									noResize
									className={
										styles["los-table__header__heading"] + " " + styles["los-table__header__learning-objective-heading"]
									}
								>
									{MarkValueFormat.name(markValue)}
								</Table.Heading>
							))}
						</Else>
					</Conditional>
				</Table.Row>
			</Table.Header>
			<Table.Body className={styles["los-table__body"]} style={{ overflow: "visible" }}>
				{items.map((item) => (
					<RowComponent
						key={item.id}
						rowVariant={rowVariant}
						item={item}
						markValues={learningObjectiveCategory.markingSystem.markValues}
						isSingleMarkValueColumn={isSingleMarkValueColumn}
						NameCellComponent={NameCellComponent}
						disabled={disabled || disabledItemIDs.has(item.id)}
					/>
				))}
			</Table.Body>
		</Table>
	);
}

export type LearningObjectivesTableLearningObjectiveNameCellProps = {
	item: {
		learningObjective: LearningObjectiveNameFragment & Pick<LearningObjective, "description">;
	};
};

export function LearningObjectivesTableLearningObjectiveNameCell({
	item,
}: LearningObjectivesTableLearningObjectiveNameCellProps) {
	return (
		<Table.Cell
			column="name"
			className={styles["los-table__body__cell"] + " " + styles["los-table__body__learning-objective-cell"]}
		>
			<Column>
				<div className={styles["los-table__body__learning-objective__name"]}>
					{LearningObjectiveFormat.name(item.learningObjective)}
				</div>
				<If condition={!!item.learningObjective.description}>
					<Rule thickness="2px" color="var(--neutral-colors--stroke)" />
					<div className={styles["los-table__body__learning-objective__description"]}>
						{item.learningObjective.description}
					</div>
				</If>
			</Column>
		</Table.Cell>
	);
}

export type LearningObjectivesTableStudentNameCellProps = {
	item: {
		student: Pick<Student, "id"> & StudentNameAndImageFragment;
	};
};

export function LearningObjectivesTableStudentNameCell({ item }: LearningObjectivesTableStudentNameCellProps) {
	return (
		<Table.Cell column="name" className={styles["los-table__body__cell"]}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
				<StudentIcon student={item.student} size={32} />
				<span className={styles["los-table__body__student__name"]}>{StudentFormat.name(item.student)}</span>
			</Row>
		</Table.Cell>
	);
}

export type LearningObjectivesTableOverallMarkNameCellProps = {
	item: {
		reportCardSectionPolicy: Pick<ReportCardSectionPolicy, "id" | "overallMarkLabel">;
	};
};

export function LearningObjectivesTableOverallMarkNameCell({ item }: LearningObjectivesTableOverallMarkNameCellProps) {
	return (
		<Table.Cell column="name" className={styles["los-table__body__cell"]}>
			<div className={styles["los-table__body__learning-objective__name"]}>
				{ReportCardSectionPolicyFormat.Fields.overallMarkLabel(item.reportCardSectionPolicy.overallMarkLabel)}
			</div>
		</Table.Cell>
	);
}
